import { type InvertedLocalize } from '../utils/localization';
import { type invertedMessages } from '../utils/messages';

import config from './ispmConfig';

// NODE_ENV appears to be misreporting 'development' in host.
export const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === 'loopback' ||
  window.location.port.startsWith('300');

export function strToBool(str?: string): boolean {
  if (!str || str.length === 0) {
    return false;
  }

  if (str === 'true') {
    return true;
  }

  if (str === 'false') {
    return false;
  }

  throw new Error("Invalid input: must be 'true' or 'false'");
}

export function isValidUrl(url?: string): boolean {
  if (!url || url.length === 0) {
    return false;
  }

  const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;

  return urlPattern.test(url);
}

export const ispmEnvironmentSettings = {
  SAVI_INPUT_PLACEHOLDER: 'Type a message and press Enter...',
  ECM_URL: window.location.origin,
  ISPM_BASE_ROUTE: config.DASHBOARD_URL,
  HEADER_INPUT_FIELD_CLICK_DISPATCH_MESSAGE: 'SHOW_SUGGESTIONS',
};

const BASE_URL = `${window.location.origin}/ECMv6/api/ispm`;

export const TABLE_MAX_ROWS = 100;
export const SIDE_PANEL_LOADER_TIME_INTERVAL = 5000;
export const SIDE_PANEL_LOADER_TEST_TIME_INTERVAL = 500;

export const getSaviApiUrlLists = (useMockServer: boolean) => ({
  CHAT_SUGGESTIONS_URL: useMockServer
    ? `${BASE_URL}/mock/chat/suggestions`
    : `${BASE_URL}/chat/suggestions`,

  CHAT_ASK_URL: useMockServer
    ? `${BASE_URL}/mock/chat/ask`
    : `${BASE_URL}/chat/ask`,

  DATA_SAMPLE_URL: useMockServer
    ? `${BASE_URL}/mock/data/sample`
    : `${BASE_URL}/data/sample`,

  DATA_DOWNLOAD_URL: useMockServer
    ? `${BASE_URL}/mock/data/download`
    : `${BASE_URL}/data/download`,

  DASHBOARD_LIST_URL: useMockServer
    ? `${BASE_URL}/mock/dashboard/list`
    : `${BASE_URL}/dashboard/list`,

  DASHBOARD_URL: useMockServer
    ? `${BASE_URL}/mock/dashboard`
    : `${BASE_URL}/dashboard`,
});

export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  delay: number
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debouncedFunction = (...args: Parameters<F>): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };

  debouncedFunction.cancel = (): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFunction;
};

export const getGeneratingContentLoadingMsgs = (
  l: InvertedLocalize<keyof typeof invertedMessages>
): Record<string, string[]> => ({
  [l('Generating SQL')]: [
    l('Capturing user input'),
    l('Identifying schema'),
    l('Parsing parameters'),
    l('Constructing SQL query'),
    l('Validating generated SQL'),
  ],
  [l('Generating Table Data')]: [
    l('Executing SQL'),
    l('Fetching data'),
    l('Formatting data'),
    l('Transforming data'),
    l('Finalizing table'),
  ],
  [l('Generating Chart')]: [
    l('Selecting chart type'),
    l('Configuring chart'),
    l('Mapping data'),
    l('Styling chart'),
    l('Generating visualization'),
  ],
});

export const getGeneratingContentLoadingMsgsForTesting = (
  l: InvertedLocalize<keyof typeof invertedMessages>
): Record<string, string[]> => ({
  [l('Generating SQL')]: [l('Capturing user input'), l('Identifying schema')],
  [l('Generating Table Data')]: [l('Executing SQL'), l('Fetching data')],
});

export const getAppropriateGeneratingContentLoadingMsgs = (
  l: InvertedLocalize<keyof typeof invertedMessages>
) => {
  const generatingContentLoadingMsgs = getGeneratingContentLoadingMsgs(l);
  const generatingContentLoadingMsgsForTesting =
    getGeneratingContentLoadingMsgsForTesting(l);

  return process.env.NODE_ENV === 'test'
    ? generatingContentLoadingMsgsForTesting
    : generatingContentLoadingMsgs;
};

export const getGeneratingContentLoadingMsgsTimeInterval = () =>
  process.env.NODE_ENV === 'test'
    ? SIDE_PANEL_LOADER_TEST_TIME_INTERVAL
    : SIDE_PANEL_LOADER_TIME_INTERVAL;

export const SCROLL_THRESHOLD = 120; // Adjust scroll visibility threshold (in px)

export const CHAT_SKELETON_WIDTHS = [
  '80PercentWidth',
  '30PercentWidth',
  '80PercentWidth',
  '50PercentWidth',
  '80PercentWidth',
  '40PercentWidth',
  '80PercentWidth',
  '30PercentWidth',
  '80PercentWidth',
];
export const CHAT_SKELETON_SCREEN_DELAY_MS = 800;
